import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import moment from 'moment';
import Container from '../../../../common/components/container';
import Spinner from '../../../../common/components/spinner';
import { DEFAULT_SEND_EVENT_START_REMINDER, EventForm } from '../../forms/event';
import * as predicateUtil from '../../../../common/utils/predicates';
import * as eventActions from '../../actions';
import * as eventsSelector from '../../selector';
import type { StoreState } from '@common/types/store';
import type { Event } from '../../types';
import { EEventType } from '@modules/events/definitions';
import { getInitValue } from '@common/components/intervals-list-input/utils';

interface Props extends RouteComponentProps<{ id?: string, networkId?: string }> {
  event?: Event,
}

const createInitialValues = (event?: Event) => {
  if (event) {
    const startTime = moment(event.start_time);
    const endTime = event.end_time ? moment(event.end_time) : startTime.add(1, 'hour');
    const deadline = event.settings.registration_deadline && moment(event.settings.registration_deadline);

    const sendEventStartReminder = getInitValue(event.settings.send_event_start_reminder);

    const values: Record<string, any> = {
      start_date: startTime,
      start_time: startTime.format('HH:mm'),
      is_full_day: event.is_full_day,
      end_date: endTime,
      end_time: endTime.format('HH:mm'),
      location: event.location,
      name: event.name,
      web_link: event.web_link,
      description: event.description,
      unlimited_spots: event.settings.max_guest_limit === null,
      has_deadline: event.settings.registration_deadline !== null,
      header_image: event.header_image_url
        ? {
          path: event.header_image_url,
          is_remote_file: event.is_header_image_remote_file,
          type: 'image',
        }
        : null,
      settings: {
        hide_guest_list: event.settings.hide_guest_list,
        disable_guest_list: event.settings.disable_guest_list,
        only_admins_can_post: event.settings.only_admins_can_post,
        max_guest_limit: event.settings.max_guest_limit,
        send_event_created_notification: event.settings.send_event_created_notification,
        send_event_availability_reminder_notification:
          event.settings.send_event_availability_reminder_notification,
        send_event_start_reminder_toggle: sendEventStartReminder?.length > 0,
        send_event_start_reminder: sendEventStartReminder,
        registration_deadline_date: deadline,
        registration_deadline_time: deadline && deadline.format('HH:mm'),
        allow_attendance_change_after_deadline: event.settings.allow_attendance_change_after_deadline,
      },
      invite_type: event.invite_type
    };

    // @ts-expect-error
    const audience = event?.audience?.[0];
    values.audience = {
      predicate_type: audience?.predicate_type || 'match_any',
      predicates: audience?.predicates || []
    };

    return values;
  }

  let start = moment().add(2, 'hour').minute(0);

  // When creating an event in the evening default select the next day
  if (start.hours() > 18) start = moment().add(1, 'day').hours(9).minute(0);

  const end = start.clone().add(1, 'hour');

  return {
    name: '',
    description: '',
    invite_type: EEventType.DIRECT,
    start_date: start,
    start_time: start.format('HH:mm'),
    end_date: end,
    end_time: end.format('HH:mm'),
    location: null,
    is_full_day: false,
    header_image: null,
    header_image_url: null,
    audience: {
      predicate_type: predicateUtil.ESearchPredicateTypes.MATCH_ANY,
      predicates: [],
    },
    unlimited_spots: true,
    has_deadline: false,
    settings: {
      max_guest_limit: null,
      hide_guest_list: false,
      only_admins_can_post: false,
      send_event_created_notification: true,
      send_event_availability_reminder_notification: true,
      send_event_start_reminder_toggle: true,
      send_event_start_reminder: DEFAULT_SEND_EVENT_START_REMINDER,
      registration_deadline_date: null,
      registration_deadline_time: null,
      allow_attendance_change_after_deadline: false,
    },
  };
};

const mapStateToProps = (state: StoreState, props: Props) => ({
  event: props.match.params.id ? eventsSelector.getAdminScopeEvent(state, props.match.params.id) : undefined,
});

const connector = connect(mapStateToProps);

export const EventFormContainer = connector(({ match: { params }, event }: Props) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (params.id && !event) dispatch(eventActions.fetchEvent(params.id));
    window.scrollTo(0, 0);
  }, [params.id]);

  if (params.id && !event) {
    return (
      <Container name="EventForm">
        <Container.Content>
          <Spinner size="large" centered />
        </Container.Content>
      </Container>
    );
  }

  const initialValues = createInitialValues(event);
  return (
    <Container name="EventForm">
      <EventForm
        initialValues={initialValues}
        // @ts-expect-error
        event={event}
      />
    </Container>
  );
});
